import { useMemo } from "react";
import { Button, Typography } from "@mui/material";
import moment from "moment";
import { useTranslation } from "react-i18next";

import { IMainTableColumn } from "components/MainTable/MainTableInterfaces";
import { IThirdPartyApiKey } from "api/queries/apiKeysAPI";
import { useStyles } from "../APIKeyManagement.styles";
import { ApiKeyTableColumnsIdEnum, IUseApiKeyColumnsParams } from "../APIKeyManagementInterfaces";

const KEYS = "corporate_backoffice.api_key_management";

// Near-expiry cue window (per Jesus, 2026-07-01) — visual only; expiry itself is unchanged.
const EXPIRY_WARNING_DAYS = 30;

type KeyStatus = "active" | "expired" | "revoked";

const getKeyStatus = (row: IThirdPartyApiKey): KeyStatus => {
    if (row.revoked_at) {
        return "revoked";
    }
    if (row.expires_at && moment(row.expires_at).isBefore(moment())) {
        return "expired";
    }
    return "active";
};

/**
 * Column definitions for the CBO API-key table (house convention: columns live in a hook, not
 * inline in the view). Owns status derivation, the near-expiry cue, and the row actions; the
 * view supplies the permission flags + rotate/revoke handlers.
 */
export const useApiKeyColumns = ({
    canGenerate,
    canRevoke,
    onRevoke,
    onRotate,
}: IUseApiKeyColumnsParams): IMainTableColumn[] => {
    const classes = useStyles();
    const { t } = useTranslation();

    return useMemo((): IMainTableColumn[] => {
        const header = (key: string) => (
            <Typography variant="subtitle2" className={classes.columnHeader}>
                {t(`${KEYS}.${key}`).toUpperCase()}
            </Typography>
        );

        const renderStatus = (row: IThirdPartyApiKey) => {
            const status = getKeyStatus(row);
            const label = t(`${KEYS}.status_${status}`);
            if (status !== "active") {
                return label;
            }
            const daysLeft = moment(row.expires_at).diff(moment(), "days");
            if (daysLeft > EXPIRY_WARNING_DAYS) {
                return label;
            }
            const warning =
                daysLeft <= 0
                    ? t(`${KEYS}.expires_today`)
                    : daysLeft === 1
                    ? t(`${KEYS}.expires_in_day`)
                    : t(`${KEYS}.expires_in_days`, { count: daysLeft });
            return (
                <span className={classes.expiringSoon}>
                    {label} · {warning}
                </span>
            );
        };

        const renderActions = (row: IThirdPartyApiKey) => {
            if (getKeyStatus(row) === "revoked") {
                return <span>—</span>;
            }
            return (
                <div className={classes.actions}>
                    {canGenerate && (
                        <Button
                            className={classes.fontFamily}
                            size="small"
                            color="info"
                            onClick={() => onRotate(row)}
                        >
                            {t(`${KEYS}.rotate`)}
                        </Button>
                    )}
                    {canRevoke && (
                        <Button
                            className={classes.fontFamily}
                            size="small"
                            color="error"
                            onClick={() => onRevoke(row)}
                        >
                            {t(`${KEYS}.revoke`)}
                        </Button>
                    )}
                </div>
            );
        };

        return [
            {
                align: "left",
                id: ApiKeyTableColumnsIdEnum.LABEL,
                label: header("column_label"),
                render: (row) => (row as IThirdPartyApiKey).label || "—",
            },
            {
                id: ApiKeyTableColumnsIdEnum.KEY_PREFIX,
                label: header("column_key_prefix"),
                render: (row) => (row as IThirdPartyApiKey).key_prefix,
            },
            {
                id: ApiKeyTableColumnsIdEnum.CREATED_AT,
                label: header("column_created_at"),
                render: (row) => moment((row as IThirdPartyApiKey).created_at).format("YYYY-MM-DD"),
            },
            {
                id: ApiKeyTableColumnsIdEnum.EXPIRES_AT,
                label: header("column_expires_at"),
                render: (row) => {
                    const key = row as IThirdPartyApiKey;
                    return key.expires_at ? moment(key.expires_at).format("YYYY-MM-DD") : t(`${KEYS}.no_expiry`);
                },
            },
            {
                id: ApiKeyTableColumnsIdEnum.LAST_USED_AT,
                label: header("column_last_used_at"),
                render: (row) => {
                    const key = row as IThirdPartyApiKey;
                    return key.last_used_at
                        ? moment(key.last_used_at).format("YYYY-MM-DD HH:mm")
                        : t(`${KEYS}.never_used`);
                },
            },
            {
                id: ApiKeyTableColumnsIdEnum.STATUS,
                label: header("column_status"),
                render: (row) => renderStatus(row as IThirdPartyApiKey),
            },
            {
                align: "right",
                id: ApiKeyTableColumnsIdEnum.ACTIONS,
                label: header("column_actions"),
                render: (row) => renderActions(row as IThirdPartyApiKey),
            },
        ];
    }, [classes, t, canGenerate, canRevoke, onRevoke, onRotate]);
};
